import { ReactNode, useEffect, useState } from "react";
import clsx from "clsx";
import { useT } from "@app/contexts/preferences.context";
import Pagination from "@app/components/pagination";
import { IcReceipt, IcSearch } from "@app/components/icons";
import { CopyButton, LoadingBlock, StatusPill, btn, btnGhost, btnSm, input } from "@app/components/ui";
import { MAX_LEN } from "@app/constants";

/** Ô tìm kiếm có chờ 300ms như các trang đơn hàng cũ */
export function useDebounced(value: string, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(timer);
  }, [value]);

  return debounced;
}

export interface StatusTab<T> {
  value: T;
  label: string;
  count?: number;
}

interface OrdersShellProps<T> {
  search?: { value: string; placeholder: string; onChange: (v: string) => void };
  dates?: { from: string; to: string; onChange: (from: string, to: string) => void };
  tabs?: { value: T; items: StatusTab<T>[]; onChange: (v: T) => void };
  headers: string[];
  empty: boolean;
  /** Chưa có dữ liệu lần đầu: hiện "Đang tải…" thay cho "Chưa có đơn hàng nào" */
  loading?: boolean;
  filtered: boolean;
  page: number;
  totalPages: number;
  count: number;
  onPageChange: (page: number) => void;
  children: ReactNode;
}

/** Khung chung của bảng đơn hàng: thanh lọc, bảng, phân trang */
export function OrdersShell<T extends string | number>({
  search,
  dates,
  tabs,
  headers,
  empty,
  filtered,
  page,
  totalPages,
  count,
  onPageChange,
  children,
  loading,
}: OrdersShellProps<T>) {
  const t = useT();

  return (
    <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
      {(search || dates || tabs) && (
        <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-3 max-[760px]:px-3.5">
          {search && (
            <label className="flex h-9 min-w-[220px] flex-[1_1_240px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
              <IcSearch className="h-[18px] w-[18px] flex-none" />
              <input
                type="search"
                maxLength={MAX_LEN.SEARCH}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={t(search.placeholder)}
                className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
              />
            </label>
          )}
          {dates && (
            <div className="flex items-center gap-1.5 text-muted max-[760px]:w-full max-[760px]:flex-wrap">
              <input
                type="date"
                aria-label={t("Từ ngày")}
                className={clsx(input, "!h-9 w-[150px] max-[760px]:w-auto max-[760px]:min-w-0 max-[760px]:flex-1 max-[400px]:basis-full")}
                value={dates.from}
                onChange={(e) => dates.onChange(e.target.value, dates.to)}
              />
              <span aria-hidden="true" className="max-[400px]:hidden">–</span>
              <input
                type="date"
                aria-label={t("Đến ngày")}
                className={clsx(input, "!h-9 w-[150px] max-[760px]:w-auto max-[760px]:min-w-0 max-[760px]:flex-1 max-[400px]:basis-full")}
                value={dates.to}
                onChange={(e) => dates.onChange(dates.from, e.target.value)}
              />
              {(dates.from || dates.to) && (
                <button type="button" className={clsx(btn, btnGhost, btnSm, "max-[760px]:basis-full")} onClick={() => dates.onChange("", "")}>
                  {t("Xoá ngày")}
                </button>
              )}
            </div>
          )}
          {tabs && (
            <div className="rtabs inline-flex flex-wrap gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
              {tabs.items.map((tab) => (
                <button
                  key={String(tab.value)}
                  type="button"
                  aria-pressed={tabs.value === tab.value}
                  onClick={() => tabs.onChange(tab.value)}
                  className={clsx(
                    "inline-flex items-baseline gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                    tabs.value === tab.value ? "bg-surface text-fg shadow-card" : "text-muted"
                  )}
                >
                  {t(tab.label)}
                  {tab.count !== undefined && <em className="text-[11.5px] not-italic text-faint">{tab.count}</em>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {empty && loading ? (
        <LoadingBlock />
      ) : empty ? (
        <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcReceipt className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">{filtered ? t("Không tìm thấy đơn nào") : t("Chưa có đơn hàng nào")}</h3>
          <p className="m-0 text-muted">
            {filtered ? t("Thử từ khoá khác, đổi khoảng ngày hoặc chọn bộ lọc \"Tất cả\".") : t("Đơn bạn thuê sẽ hiện ở đây.")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* otbl: dưới 1100px mỗi đơn thành 1 thẻ như bản thiết kế (styles/_responsive.scss) */}
          <table className="otbl w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="border-b border-line bg-surface-2 text-xs font-semibold text-muted">
                {headers.map((h) => (
                  <th key={h} scope="col" className="whitespace-nowrap px-4 py-2.5 font-semibold">
                    {t(h)}
                  </th>
                ))}
                <th scope="col" className="px-4 py-2.5">
                  <span className="sr-only">{t("Lịch sử")}</span>
                </th>
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-[13px] text-muted">
          <span className="tabular-nums">
            {count} {t("đơn")}
          </span>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />}
        </div>
      )}
    </section>
  );
}

/** 1 ô trong bảng: dạng thẻ (< 1100px) hiện nhãn cột ở trên giá trị */
export function Cell({ label, className, children }: { label?: string; className?: string; children: ReactNode }) {
  const t = useT();

  return (
    <td data-label={label ? t(label) : undefined} className={clsx("px-4 py-3 align-top", className)}>
      <div className="flex min-w-0 flex-col gap-0.5">{children}</div>
    </td>
  );
}

export function Row({ ended, children }: { ended?: boolean; children: ReactNode }) {
  return (
    <tr
      className={clsx(
        "border-b border-line last:border-b-0 hover:bg-surface-2",
        ended && "text-muted"
      )}
    >
      {children}
    </tr>
  );
}

/** Ô đầu: tên, ID có nút sao chép, IP */
export function NameCell({ label, name, sub, id, ip }: { label: string; name: string; sub?: string; id: string; ip?: string }) {
  return (
    <Cell label={label} className="o-m max-w-[260px]">
      <b className="truncate text-fg">{name}</b>
      {sub && <small className="truncate text-xs text-muted">{sub}</small>}
      <span className="flex items-center gap-0.5 text-[11.5px] text-muted">
        ID: <span className="font-mono" title={id}>{id.length > 14 ? id.slice(0, 12) + "…" : id}</span>
        <CopyButton value={id} label="Sao chép ID" />
      </span>
      {ip && (
        <span className="text-[11.5px] text-muted">
          IP: <span className="font-mono">{ip}</span>
        </span>
      )}
    </Cell>
  );
}

export function StateCell({
  label,
  tone,
  text,
  sub,
}: {
  label: string;
  tone: "ok" | "warn" | "bad" | "muted";
  text: string;
  sub?: string;
}) {
  const t = useT();

  return (
    <Cell label={label}>
      <span>
        <StatusPill tone={tone}>{t(text)}</StatusPill>
      </span>
      {sub && <small className="text-xs text-muted">{t(sub)}</small>}
    </Cell>
  );
}

export function HistoryCell({ onClick }: { onClick?: () => void }) {
  const t = useT();

  return (
    <td className="o-a px-4 py-3 text-right align-top">
      {onClick && (
        <button type="button" className={clsx(btn, btnGhost, btnSm)} onClick={onClick}>
          {t("Lịch sử")}
        </button>
      )}
    </td>
  );
}
